/**
 * RevenueCat Web Service
 * 
 * Note: The RevenueCat JS SDK (@revenuecat/purchases-js) is designed specifically 
 * for Web Billing with Stripe integration. It has a different API than the mobile SDKs.
 * This service provides a placeholder implementation until we can properly configure
 * Web Billing in the RevenueCat dashboard and integrate with Stripe.
 */

import { 
  IAPService, 
  IAPOffering, 
  IAPCustomerInfo, 
  IAPPurchaseResult, 
  IAPRestoreResult,
  IAPSubscriptionStatus
} from './types';
import { getSDKKey, REVENUECAT_CONFIG } from './config';
import logger from '../../utils/logger';

class RevenueCatWebService implements IAPService {
  private isInitialized = false;
  private userId: string | null = null;

  async initialize(userId: string): Promise<void> {
    try {
      if (this.isInitialized) {
        logger.info('RevenueCat Web already initialized');
        return;
      }

      const sdkKey = getSDKKey('web');
      this.userId = userId;
      
      // TODO: Implement proper RevenueCat Web SDK initialization once we have:
      // 1. Stripe account connected in RevenueCat dashboard
      // 2. Web Billing app created in RevenueCat
      // 3. Products and offerings configured for Web
      // 4. Proper API endpoints set up
      
      logger.info('RevenueCat Web initialization placeholder', { userId, sdkKey });
      this.isInitialized = true;
    } catch (error) {
      logger.error('Failed to initialize RevenueCat Web', error);
      throw error;
    }
  }

  async getOfferings(): Promise<IAPOffering[]> {
    if (!this.isInitialized) {
      throw new Error('RevenueCat Web not initialized');
    }

    // TODO: Implement once Web Billing is configured
    // This will require calling a backend endpoint that uses RevenueCat's REST API
    // or implementing the RevenueCat JS SDK once we understand its API better
    
    logger.info('Web offerings requested - returning placeholder data');
    
    // Return placeholder offerings that match the mobile offerings
    return [{
      identifier: 'default',
      serverDescription: 'Default Web Offering',
      availablePackages: [
        {
          identifier: 'peak_monthly',
          packageType: '$rc_monthly',
          product: {
            identifier: 'monthly',
            priceString: '$4.99',
            price: 4.99,
            currencyCode: 'USD',
            title: 'Peak Monthly',
            description: 'Monthly Peak subscription',
            introPrice: undefined
          }
        },
        {
          identifier: 'peak_annual',
          packageType: '$rc_annual',
          product: {
            identifier: 'yearly',
            priceString: '$39.99',
            price: 39.99,
            currencyCode: 'USD',
            title: 'Peak Annual',
            description: 'Annual Peak subscription (save 33%)',
            introPrice: undefined
          }
        }
      ]
    }];
  }

  async getCustomerInfo(): Promise<IAPCustomerInfo> {
    if (!this.isInitialized) {
      throw new Error('RevenueCat Web not initialized');
    }

    // TODO: Implement backend endpoint to get customer info from RevenueCat REST API
    
    // For now, return empty customer info
    return {
      activeSubscriptions: [],
      allPurchasedProductIdentifiers: [],
      entitlements: {
        all: {},
        active: {}
      },
      firstSeen: new Date().toISOString(),
      originalAppUserId: this.userId || ''
    };
  }

  async purchasePackage(packageId: string): Promise<IAPPurchaseResult> {
    if (!this.isInitialized) {
      throw new Error('RevenueCat Web not initialized');
    }

    // TODO: Implement web purchase flow
    // This will need to:
    // 1. Create a checkout session via backend API
    // 2. Redirect to Stripe Checkout
    // 3. Handle success/cancel callbacks
    
    logger.info('Web purchase requested', { packageId });
    
    // For now, show a message about web purchases coming soon
    return {
      success: false,
      error: {
        code: 'WEB_PURCHASES_NOT_CONFIGURED',
        message: 'Web purchases are coming soon! Please use the mobile app to subscribe for now.'
      }
    };
  }

  async restorePurchases(): Promise<IAPRestoreResult> {
    if (!this.isInitialized) {
      throw new Error('RevenueCat Web not initialized');
    }

    // TODO: Implement restore via backend API
    // This will query RevenueCat REST API for the user's purchases
    
    logger.info('Web restore requested');
    
    return {
      success: false,
      error: {
        code: 'WEB_RESTORE_NOT_CONFIGURED',
        message: 'To restore purchases on web, please log in with the same account you used on mobile.'
      }
    };
  }

  async getSubscriptionStatus(): Promise<IAPSubscriptionStatus> {
    try {
      if (!this.isInitialized) {
        // Return default status if not initialized
        return {
          isPeakSubscriber: false,
          isAdFree: false,
          activeSubscriptions: [],
          entitlements: {
            all: {},
            active: {}
          }
        };
      }

      // TODO: Implement via backend API
      // For now, we'll rely on the Firebase isPeakSub field
      // which is synced from mobile purchases
      
      return {
        isPeakSubscriber: false,
        isAdFree: false,
        activeSubscriptions: [],
        entitlements: {
          all: {},
          active: {}
        }
      };
    } catch (error) {
      logger.error('Failed to get subscription status', error);
      
      // Return default status on error
      return {
        isPeakSubscriber: false,
        isAdFree: false,
        activeSubscriptions: [],
        entitlements: {
          all: {},
          active: {}
        }
      };
    }
  }

  async logOut(): Promise<void> {
    try {
      // Clear any cached data
      this.userId = null;
    } catch (error) {
      logger.error('Error during RevenueCat logout', error);
    } finally {
      this.isInitialized = false;
    }
  }

  isAvailable(): boolean {
    // Check if we're on web and web purchases are enabled
    return typeof window !== 'undefined' && 
           !(window as any).Capacitor && 
           REVENUECAT_CONFIG.featureFlags.enableWebPurchases;
  }
}

export const revenueCatWebService = new RevenueCatWebService();